import classNames from "classnames";
import { ChangeEvent } from "cleave.js/react/props";
import uniqueId from "lodash/uniqueId";
import { Label, LabelIcons } from "PFComponents/label";
import { LockedIcon } from "PFComponents/text/input_field_set/components/locked_icon";
import { Tip } from "PFComponents/text/input_field_set/components/tip";
import { EventKey } from "PFTypes/event_key";
import { CSSProperties, KeyboardEventHandler, MouseEventHandler, RefObject, useCallback } from "react";

import css from "./toggle.module.scss";
import { ToggleReadOnlyIcon } from "./toggle_read_only_icon";

export type ToggleProps = {
  label?: string;
  labelIcons?: LabelIcons;
  tip?: string;
  inline?: boolean;
  compact?: boolean;
  qaId?: string;
  checked?: boolean;
  disabled?: boolean;
  description?: string;
  onChange?: (value: boolean) => void;
  reversed?: boolean;
  toggleReversed?: boolean;
  error?: string;
  readOnly?: boolean;
  locked?: boolean;
  lockedTip?: string;
  className?: string;
  required?: boolean;
  title?: string;
  variant?: "default" | "table";
  tabIndex?: number;
  style?: CSSProperties;
  switchRef?: RefObject<HTMLDivElement>;
};

const Toggle = ({
  label = "",
  labelIcons,
  tip = "",
  inline,
  qaId = "Toggle",
  checked = false,
  disabled,
  description,
  onChange,
  reversed,
  toggleReversed,
  error,
  readOnly,
  locked,
  lockedTip,
  compact,
  className,
  required,
  title,
  variant = "default",
  tabIndex,
  style,
  switchRef
}: ToggleProps) => {
  const toggleId = uniqueId("toggle");
  const labelId = `${toggleId}-label`;
  const descriptionId = `${toggleId}-description`;
  const ariaLabelledBy = label ? labelId : description ? descriptionId : undefined;

  const handleChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      if (readOnly || locked) {
        return;
      }

      event.stopPropagation();
      onChange?.(event.target.checked);
    },
    [readOnly, locked, onChange]
  );

  const toggle = useCallback(() => {
    onChange?.(!checked);
  }, [checked, onChange]);

  const handleClick: MouseEventHandler<HTMLDivElement> = useCallback(
    (event) => {
      if (readOnly || locked) {
        return;
      }

      event.stopPropagation();
      toggle();
    },
    [readOnly, locked, toggle]
  );

  const handleKeyDown: KeyboardEventHandler<HTMLDivElement> = useCallback(
    (event) => {
      if (event.key === EventKey.Enter && !disabled && !locked) {
        toggle();
      }
    },
    [toggle, disabled, locked]
  );

  return (
    <div
      className={classNames(css.root, className, { [css.tableVariant]: variant === "table" })}
      style={style}
    >
      <div
        className={classNames(css.toggleContainer, {
          [css.readOnly]: readOnly,
          [css.errorLabel]: error,
          [css.labelInline]: inline,
          [css.labelVertical]: !inline,
          [css.disabled]: disabled || locked,
          [css.reversed]: reversed
        })}
      >
        <Label
          text={label}
          icons={labelIcons}
          inputId={toggleId}
          id={labelId}
          className={css.label}
          required={required}
          withMargin
        />
        <div
          ref={switchRef}
          title={title}
          role="switch"
          aria-labelledby={ariaLabelledBy}
          aria-checked={checked}
          className={classNames(css.inputContainer, { [css.compact]: compact })}
          style={{
            flexDirection: toggleReversed ? "row-reverse" : "row"
          }}
          data-qa-id={qaId}
          onClick={handleClick}
          onKeyDown={handleKeyDown}
          tabIndex={tabIndex ?? 0}
        >
          {readOnly ? (
            <ToggleReadOnlyIcon checked={checked} />
          ) : (
            <>
              <input
                id={toggleId}
                className={css.input}
                type="checkbox"
                checked={checked}
                disabled={disabled || locked}
                onChange={handleChange}
                aria-required={required}
                data-qa-id={`${qaId}-Input`}
              />
              <div className={css.switch} />
            </>
          )}
          {description && (
            <Label
              text={description}
              required={required}
              inputId={toggleId}
              id={descriptionId}
              className={css.description}
              variant="bodyRegular"
            />
          )}
          {locked && <LockedIcon className={css.lockedIcon} lockedTip={lockedTip} />}
        </div>
      </div>

      {(error || tip) && <Tip tip={tip} error={error} />}
    </div>
  );
};

export default Toggle;
